import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ICardVerificationDocument,
  ICardVerificationForm,
  IDocumentStep,
  INoteChangeVerification,
  IPayloadRegistrationVerification,
} from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import { documentBodyData, EnumCardVerificationForm, EnumCardVerificationSection } from '../distributor-data/distributor-data.data';
import { FormGroup, Validators } from '@angular/forms';
import { DistributorStatusEnum, DistributorTypeEnum } from '@config/enum/distributor.enum';
import { ActivatedRoute, Params } from '@angular/router';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';
import { ICardDetailBody } from '@models/card.model';
import { IDistributorDocument } from '../../../../interfaces/distributor.interface';
import { UtilitiesService } from '@services/utilities.service';
import { RolePrivilegeService } from '@services/role-privilege.service';
import { IGenericNameUrl } from '@shared/interface/generic';
import { BaseService } from '@services/base-service.service';
import { API } from '@config/constants/api.constant';

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss'],
})
export class DocumentComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isViewOnly = false;
  @Input() status!: DistributorStatusEnum | string;

  snapshotParams!: Params;

  bodyDataDocument: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataUpdateDocument: BehaviorSubject<ICardVerificationDocument> = new BehaviorSubject<ICardVerificationDocument>({} as ICardVerificationDocument);

  dataValueDocumentStep: BehaviorSubject<IDocumentStep> = new BehaviorSubject<IDocumentStep>({} as IDocumentStep);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  revisionNoteData: IPayloadRegistrationVerification;
  revisionType: EnumDistributorRevisionSession;

  dataValueNonActive: BehaviorSubject<IDistributorDocument> = new BehaviorSubject<IDistributorDocument>({} as IDistributorDocument);
  detailDocumentDistributor: ICardDetailBody[];
  detailDocumentNpwp: ICardDetailBody[];
  detailDocumentActivation: ICardDetailBody[];
  documentNonActive: IGenericNameUrl[] = [];
  privilegeConfidentialDocuments: Array<any>;

  get DistributorDetailHeader() {
    return this.detailRegistrationService.getDistributorDetailHeader();
  }

  get ShowBadge() {
    return this.detailRegistrationService.showBadgePendingDetail();
  }

  get IsNonActivated() {
    return this.detailRegistrationService.isStatusNonActivated() || this.detailRegistrationService.isStatusRejectNonActivated();
  }

  constructor(
    public detailRegistrationService: DetailRegistrationDistributorService,
    private activatedRoute: ActivatedRoute,
    private utils: UtilitiesService,
    private baseService: BaseService,
    private rolePrivilegeService: RolePrivilegeService,
    private ref: ChangeDetectorRef
  ) {
    this.snapshotParams = this.activatedRoute.snapshot.params;
    this.revisionType = this.detailRegistrationService.getTypeRevision(this.snapshotParams.tab);
    this.privilegeConfidentialDocuments = this.rolePrivilegeService.checkPrivilegeConfidential('DISTRIBUTOR', 'DETAIL_DISTRIBUTOR', 'TAB_DOCUMENTS');
  }

  ngOnInit(): void {
    this.getRevisionNoteData();
    this.getData();
    this.initPageInfo();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.detailRegistrationService.getSessionRevisionNote(this.revisionType) ?? ({} as IPayloadRegistrationVerification);
  }

  getDocumentStep() {
    this.detailRegistrationService.getDocumentStep().subscribe((value) => {
      if (!value) return;
      this.dataValueDocumentStep.next(value);

      const { revision_note, status_verified_enum, distributor_document_changes } = value.document_update_distributor;
      const _bodyData: ICardVerificationDocument = {
        document_changes: distributor_document_changes,
        revision_note: revision_note,
        status_verified_enum: status_verified_enum,
      };

      this.bodyDataUpdateDocument.next(_bodyData);
      !this.isViewOnly && this.handleValidationForm();
      this.generateBodyData(value);
      this.isLoading.next(false);
    });
  }

  generateBodyData(data: IDocumentStep) {
    const { document } = data;
    const distributorType = this.DistributorDetailHeader.business_type;
    let _documentBody: ICardVerificationForm[] = documentBodyData;

    // filter out document key for distributor type: individual
    if (distributorType && distributorType === DistributorTypeEnum.INDIVIDUAL) {
      _documentBody = _documentBody.filter(
        (item) => item.key !== 'nib_document_url' && item.key !== 'deed_incorporation_document_url' && item.key !== 'deed_amendment_document_url'
      );
    }

    const _document = this.detailRegistrationService.generateBodyData(document, _documentBody);

    this.bodyDataDocument.next(_document);
  }

  handleValidationForm() {
    const { document, document_update_distributor } = this.dataValueDocumentStep.value;
    const { revision_note } = this.revisionNoteData;

    const documentControl = this.form.get('document');
    const documentUpdateControl = this.form.get('document_update_distributor');

    if (documentControl) {
      if (this.detailRegistrationService.checkValidationFormStepper(documentControl, document) && this.detailRegistrationService.isVerifiedAndRevision(document)) {
        documentControl.clearValidators();
      } else {
        documentControl.setValidators([Validators.required]);
        documentControl.setValue(revision_note && revision_note.document);
      }
      documentControl.updateValueAndValidity();
    }
    if (documentUpdateControl) {
      if (!this.isShowDataUpdateDocument()) {
        documentUpdateControl.clearValidators();
      } else {
        if (
          this.detailRegistrationService.checkValidationFormStepper(documentUpdateControl, document_update_distributor) &&
          this.detailRegistrationService.isVerifiedAndRevision(document_update_distributor)
        ) {
          documentUpdateControl.clearValidators();
        } else {
          documentUpdateControl.setValidators([Validators.required]);
          documentUpdateControl.setValue(revision_note && revision_note.document_update_distributor);
        }
      }
      documentUpdateControl.updateValueAndValidity();
    }
  }

  noteChanged(e: INoteChangeVerification) {
    this.detailRegistrationService.setRevisionData(this.revisionNoteData, e, this.revisionType);
  }

  noteValue(type: EnumCardVerificationSection) {
    if (type === EnumCardVerificationSection.DELIVERY_ADDRESS) {
      return [];
    } else {
      const { revision_note } = this.revisionNoteData;
      if (!revision_note) return [];
      const value = revision_note[type.toLowerCase() as keyof typeof revision_note];
      !this.isViewOnly && this.handleValidationForm();
      return value;
    }
  }

  afterActionVerification(event: string | null) {
    if (event === null) {
      this.getRevisionNoteData();
    }
    setTimeout(() => {
      this.getData();
    }, 500);
  }

  isShowDataUpdateDocument() {
    return this.status === DistributorStatusEnum.PENDING_EDIT_VERIFICATION || this.isViewOnly;
  }

  getData() {
    if (this.IsNonActivated) {
      const id = this.snapshotParams.id;
      this.baseService.getData<IDistributorDocument>(API.DETAIL_DOCUMENT + id).subscribe((value) => {
        if (value) {
          this.dataValueNonActive.next(value.data);
          this.documentNonActive = value.data.document_non_active ? [value.data.document_non_active] : [{} as IGenericNameUrl];
          this.isLoading.next(false);
          this.ref.detectChanges();
        }
      });
    } else {
      this.getDocumentStep();
    }
  }

  initPageInfo() {
    this.dataValueNonActive.subscribe((value) => {
      if (!value) return;

      this.mapDocumentLabelValue(value);
    });
  }

  mapDocumentLabelValue(value: IDistributorDocument): void {
    const {
      owner_ktp_image_url,
      deed_incorporation_image_url,
      deed_amendment_image_url,
      npwp_image_url,
      nib_image_url,
      place_business_image_url,
      pkp_image_url,
      ktp_manager_image_url,
      assigned_kul_image_url,
      other_image_url,
      distributor_assigned_kul_image_url,
      integrity_pact_image_url,
    } = value;

    const createDocumentArray = (
      entries: {
        label: string;
        name: string;
        value: string | null | undefined;
      }[]
    ) => entries.filter((entry) => !!entry.value); // Optional: filter out null/undefined

    this.detailDocumentDistributor = this.utils.privilegeConfidentialDocuments(
      this.privilegeConfidentialDocuments,
      createDocumentArray([
        { label: 'KTP Pemilik Usaha :', name: 'CARD_PIC_KTP_PEMILIK', value: owner_ktp_image_url },
        { label: 'KTP Pengelola Usaha :', name: 'CARD_PIC_KTP_PENGELOLA', value: ktp_manager_image_url },
        { label: 'Akta Pendirian:', name: 'CARD_PIC_AKTA_PENDIRIAN', value: deed_incorporation_image_url },
        { label: 'Akta Perubahan :', name: 'CARD_PIC_AKTA_PERUBAHAN', value: deed_amendment_image_url },
        { label: 'NIB :', name: 'CARD_PIC_NIB', value: nib_image_url },
        { label: 'Foto Tempat Usaha :', name: 'CARD_PIC_TEMPAT_USAHA', value: place_business_image_url },
      ])
    );

    this.detailDocumentNpwp = this.utils.privilegeConfidentialDocuments(
      this.privilegeConfidentialDocuments,
      createDocumentArray([
        { label: 'NPWP :', name: 'CARD_PIC_NPWP', value: npwp_image_url },
        { label: 'Surat Pernyataan non PKP :', name: 'CARD_PIC_NON_PKP', value: pkp_image_url },
      ])
    );

    this.detailDocumentActivation = createDocumentArray([
      { label: 'KUL :', name: 'CARD_PIC_KUL', value: assigned_kul_image_url },
      { label: 'Foto TTD KUL :', name: 'CARD_PIC_SIGNING_KUL', value: distributor_assigned_kul_image_url },
      { label: 'Pakta Integritas :', name: 'CARD_PIC_INTEGRITY_PACT', value: integrity_pact_image_url },
      { label: 'Lainnya :', name: 'CARD_PIC_OTHER', value: other_image_url },
    ]);
  }

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly DistributorStatusEnum = DistributorStatusEnum;
}
